import { NgModule } from '@angular/core';
import { mapToCanActivate, mapToResolve, RouterModule, Routes } from '@angular/router';
import { AsdCallbackComponent, AuthGuardService, SsoModule, SsoOptions } from '@cbre/sso';
import { AccessRequestPageComponent } from '@core/pages/access-request/access-request.page';
import { HomePageComponent } from '@core/pages/home/home.page';
import { ActiveGuardService } from '@core/services/active-guard.service';
import { FeatureGuardService } from '@core/services/feature-guard.service';
import { InactiveGuardService } from '@core/services/inactive-guard.service';
import { RoleGuardService } from '@core/services/role-guard.service';
import { environment } from '@env/environment';
import { ErrorPageComponent } from '@shared/components/error-page/error-page.component';
import { ErrorPageGuard } from '@shared/components/error-page/error-page.guard';
import { CurrencySymbolsResolver } from '@shared/currencies';
import { ColloquialResolverService } from '@shared/locale/colloquial.resolver';
import { RolesResolver } from '@shared/resolvers/roles.resolver';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { UserMarketsResolver } from '@shared/resolvers/user-markets.resolver';
import { UserTypesResolver } from '@shared/resolvers/user-types.resolver';
import { EntityDisplayOptionsService } from './core/services/entity-display-options.resolver';
import { ReportGeneratorComponent } from './report-generator/report-generator.component';
import { KadsComponent } from './kads/kads.component';

/*
 * These routes will define which module/page will be displayed
 * inside the "home page" component.
 */
const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/records/property',
  },
  {
    path: 'admin',
    canActivate: mapToCanActivate([RoleGuardService]),
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    data: {
      expectedGroup: 'Admin User',
    },
  },
  {
    path: 'audit-history',
    canActivate: mapToCanActivate([RoleGuardService]),
    loadChildren: () => import('./audit-history/audit-history.module').then(m => m.AuditHistoryModule),
    data: {
      expectedGroup: 'Audit Search',
    },
  },
  {
    path: 'profile',
    loadChildren: () => import('./user/user.module').then(m => m.UserModule),
  },
  {
    path: 'queues',
    loadChildren: () => import('./queues/queues.module').then(m => m.QueuesModule),
    resolve: {
      symbols: mapToResolve(CurrencySymbolsResolver),
    },
  },
  {
    // WP-10..WP-12: MIQ Workflows module, lazy-loaded behind a feature-flag guard.
    // See /docs/02-MIQ-Workflows-Architecture-Design.md §11.3.
    path: 'workflows',
    loadChildren: () => import('./workflows/workflows.module').then(m => m.WorkflowsModule),
  },
  {
    path: 'records',
    loadChildren: () => import('./records/records.module').then(m => m.RecordsModule),
    resolve: {
      symbols: mapToResolve(CurrencySymbolsResolver),
      colloquial: mapToResolve(ColloquialResolverService),
    },
  },
  {
    path: 'data-stack',
    canActivate: mapToCanActivate([FeatureGuardService, RoleGuardService]),
    data: {
      expectedGroup: 'Data Stack',
      featureFlag: 'spmt-module',
      redirectTo: ['/'],
    },
    children: [
      { path: '', component: KadsComponent, pathMatch: 'full' },
      { path: '**', component: KadsComponent },
    ],
  },
  {
    path: 'reports',
    loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule),
    resolve: {
      symbols: mapToResolve(CurrencySymbolsResolver),
    },
  },
  {
    path: 'market-setup',
    loadChildren: () => import('./market-settings/market-settings.module').then(m => m.MarketSettingsModule),
    resolve: {
      userMarkets: mapToResolve(UserMarketsResolver),
    },
    data: {
      all: false,
    },
  },
  {
    path: 'error',
    component: ErrorPageComponent,
    canActivate: mapToCanActivate([ErrorPageGuard]),
  },
  {
    path: 'report-generator',
    component: ReportGeneratorComponent,
    canActivate: mapToCanActivate([FeatureGuardService]),
    data: {
      featureFlag: 'report-generator',
      redirectTo: ['/'],
    },
  },
];

/*
 * These routes are the main entrance of the app. At the moment
 * (08/28/18) there is only two main landing pages. The "Home" page
 * and the "Request Access" page.
 */
const mainRoutes: Routes = [
  { path: 'auth/adfs-callback', component: AsdCallbackComponent },
  {
    path: '',
    component: HomePageComponent,
    canActivate: mapToCanActivate([AuthGuardService, ActiveGuardService]),
    children: routes,
    resolve: {
      userInfo: mapToResolve(UserInfoResolver),
      entityDisplayOptions: mapToResolve(EntityDisplayOptionsService),
    },
  },
  {
    path: 'access-request',
    component: AccessRequestPageComponent,
    canActivate: mapToCanActivate([AuthGuardService, InactiveGuardService]),
    resolve: {
      roleOptions: mapToResolve(RolesResolver),
      userTypeOptions: mapToResolve(UserTypesResolver),
      userInfo: mapToResolve(UserInfoResolver),
    },
  },
  {
    path: 'pdf',
    data: { hideMaintenanceBanner: true },
    loadChildren: () => import('./pdf-view/pdf-view.module').then(m => m.PdfViewModule),
  },
  { path: '**', redirectTo: '/' },
];

const ssoConfig: SsoOptions = {
  redirectUrl: `${environment.baseUrl}/login`,
  tokenExchangeUrl: `${environment.baseUrl}/confirm`,
  /**
   * This attribute should hold the app host domain. It is mainly used to
   * refresh and navigate the user to the app after receiving the auth token
   * so the user can have a fresh instance of the app.
   */
  hostUrl: environment.host,
};

@NgModule({
  imports: [
    SsoModule.forRoot(ssoConfig),
    RouterModule.forRoot(mainRoutes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
